import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import callApi from "helpers/callApi";
import { getApiErrorMessage } from "utils/errors";
import {
  PACKAGING_DRAFT_STATUS_MESSAGE,
  PACKAGING_LIST_FORBIDDEN_MESSAGE,
  PACKAGING_WRITE_FORBIDDEN_MESSAGE,
} from "containers/PackagingSku/packagingAccessMessages";

const initialState = {
  products: [],
  addingProduct: false,
  addingProductError: null,
  fetchingProducts: false,
  fetchingProductsError: null,
  updatingStatus: false,
  updatingStatusError: null,
  updatingProduct: false,
  updatingProductError: null,
  deletingProduct: false,
  deletingProductError: null,
};

function asPackageList(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.packages)) return data.packages;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.data)) return data.data;
  return [];
}

export const fetchAuthorizeProducts = createAsyncThunk(
  "authorize-products/fetchProducts",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const packageList = filters.type === "package";
      const params = new URLSearchParams();
      params.append("app_id", "6");

      if (filters.search) {
        params.append("search", filters.search);
      }
      if (filters.status) {
        params.append("status", filters.status);
      }
      if (filters.active_from) {
        params.append("active_from", filters.active_from);
      }
      if (filters.active_to) {
        params.append("active_to", filters.active_to);
      }
      if (packageList) {
        params.append("type", "package");
        if (
          filters.packaging_type === "Primary" ||
          filters.packaging_type === "Shipping"
        ) {
          params.append("packaging_type", filters.packaging_type);
        }
        if (filters.website_id) {
          params.append("website_id", String(filters.website_id));
        }
      } else if (filters.type) {
        params.append("type", filters.type);
      }

      const url = `/products/?${params.toString()}`;

      const response = await callApi(url, "GET");
      if (!response.ok) {
        if (response.status === 403 && packageList) {
          return rejectWithValue(PACKAGING_LIST_FORBIDDEN_MESSAGE);
        }
        throw new Error(response.errorMessage || "Error");
      }
      const data = await response.json();
      return packageList ? asPackageList(data) : data;
    } catch (error) {
      return rejectWithValue(error?.message || "Error");
    }
  }
);

// One record by id (GET /products/{id}). Kept out of `products` so the
// paginated list state is not replaced. 400 = missing or foreign id.
export const fetchAuthorizeProductById = createAsyncThunk(
  "authorize-products/fetchProductById",
  async (productId, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/products/${encodeURIComponent(productId)}`,
        "GET"
      );
      if (!response.ok) {
        return rejectWithValue(response.errorMessage || "Error");
      }
      return await response.json();
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Error"));
    }
  }
);

export const updateProductStatus = createAsyncThunk(
  "authorize-products/updateStatus",
  async ({ productId, status }, { rejectWithValue }) => {
    try {
      const response = await callApi(`/products/${productId}/status`, "PUT", {
        status: status,
      });

      if (!response.ok) {
        const message = response.errorMessage || "Error";
        if (response.status === 400 && /draft/i.test(message)) {
          return rejectWithValue(PACKAGING_DRAFT_STATUS_MESSAGE);
        }
        return rejectWithValue(message);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Error"));
    }
  }
);

const PACKAGE_IN_USE_MESSAGE =
  "This packaging is used by a label or a document request. Remove those first, then delete the packaging.";

const packageDeleteMessage = (message) =>
  /package is in use/i.test(String(message ?? ""))
    ? PACKAGE_IN_USE_MESSAGE
    : message || "Error";

export const deleteAuthorizePackage = createAsyncThunk(
  "authorize-products/deletePackage",
  async (packageId, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `/epr/packages/${encodeURIComponent(packageId)}`,
        "DELETE"
      );
      if (!response.ok) {
        if (response.status === 403) {
          return rejectWithValue(PACKAGING_WRITE_FORBIDDEN_MESSAGE);
        }
        return rejectWithValue(packageDeleteMessage(response.errorMessage));
      }
      try {
        return await response.json();
      } catch {
        return { success: true };
      }
    } catch (error) {
      return rejectWithValue(packageDeleteMessage(getApiErrorMessage(error, "Error")));
    }
  }
);

export const deleteAuthorizeProduct = createAsyncThunk(
  "authorize-products/deleteProduct",
  async (productId, { rejectWithValue }) => {
    try {
      const response = await callApi(`/products/${productId}`, "DELETE");

      if (!response.ok) {
        return rejectWithValue(response.errorMessage || "Error");
      }
      
      try {
        const data = await response.json();
        return data;
      } catch {
        return { success: true };
      }
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Error"));
    }
  }
);

const withOptionalWebsiteId = (body, productData) => {
  if (Number.isInteger(productData?.website_id)) {
    return { ...body, website_id: productData.website_id };
  }
  return body;
};

const SKU_WIZARD_FIELDS = [
  "name",
  "packaging_type",
  "total_weight",
  "length",
  "height",
  "width",
  "data",
];

const authorizeProductRequestBody = (productData) => {
  const body = {
    sku: productData.sku || "",
    description: productData.description || "",
    active_from: productData.active_from || null,
    active_to: productData.active_to || null,
    app: [6],
    comment: productData.comment || "",
    final: productData.final !== false,
    type:
      productData.type === "package" ||
      productData.type === "product" ||
      productData.type === "Primary" ||
      productData.type === "Shipping"
        ? productData.type
        : "product",
    target_market: productData.target_market ?? null,
    representative_type: productData.representative_type ?? null,
    representative_company_name:
      productData.representative_company_name ?? "",
    representative_address_line:
      productData.representative_address_line ?? "",
    representative_postal_code:
      productData.representative_postal_code ?? "",
    representative_city: productData.representative_city ?? "",
    representative_email: productData.representative_email ?? "",
  };

  const countryCode = String(productData?.representative_country_id ?? "").trim();
  if (countryCode !== "" && countryCode !== "None") {
    body.representative_country_id = countryCode;
  }
  if (productData?.available_in_invoice !== undefined) {
    body.available_in_invoice = Boolean(productData.available_in_invoice);
  }

  SKU_WIZARD_FIELDS.forEach((key) => {
    if (productData?.[key] !== undefined) {
      body[key] = productData[key];
    }
  });

  body.final = productData.final === false ? false : true;

  return withOptionalWebsiteId(body, productData);
};

const PACKAGE_PRODUCT_TYPES = new Set(["package", "Primary", "Shipping"]);

const SHARED_PARTY_PUT_OMIT_KEYS = [
  "target_market",
  "representative_type",
  "representative_company_name",
  "representative_address_line",
  "representative_postal_code",
  "representative_city",
  "representative_email",
  "representative_country_id",
];

const isAuthorizePackageType = (type) => PACKAGE_PRODUCT_TYPES.has(type);

const packagingKind = (productData) =>
  [productData?.type, productData?.packaging_type].find(
    (value) => value === "Primary" || value === "Shipping"
  );

const positiveNumber = (value) => {
  if (value == null || value === "") return null;
  const amount = Number(value);
  return Number.isFinite(amount) && amount > 0 ? amount : null;
};

// Packaging writes are partial. Omitted keys stay as stored. Dates, description
// and comment are product fields; sending active_to: null would clear a date.
const packagingWriteBody = (productData, { create }) => {
  const body = { app: [6] };
  const name = String(productData?.name ?? "").trim();
  if (name) body.name = name;
  const kind = packagingKind(productData);
  if (kind) body.type = kind;
  if (productData?.total_weight !== undefined) {
    body.total_weight = productData.total_weight;
  }
  if (Object.prototype.hasOwnProperty.call(productData || {}, "sku")) {
    body.sku = productData.sku == null ? "" : productData.sku;
  }
  const websiteId = Number(productData?.website_id);
  if (Number.isInteger(websiteId) && websiteId > 0) {
    body.website_id = websiteId;
  }
  ["length", "height", "width"].forEach((key) => {
    const amount = positiveNumber(productData?.[key]);
    if (amount != null) body[key] = productData[key];
  });
  if (productData?.data && typeof productData.data === "object") {
    body.data = productData.data;
  }
  ["active_from", "active_to"].forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(productData || {}, key)) {
      body[key] = productData[key] ? productData[key] : null;
    }
  });
  if (create) {
    body.final = productData?.final !== false;
  } else if (productData?.final === true) {
    body.final = true;
  }
  return body;
};

const authorizeProductUpdateRequestBody = (productData) => {
  const body = authorizeProductRequestBody(productData);
  if (isAuthorizePackageType(body.type)) {
    SHARED_PARTY_PUT_OMIT_KEYS.forEach((key) => {
      delete body[key];
    });
    // Packaging PUT leaves the Packaging ID unchanged when sku is omitted.
    // An explicit empty sku clears it.
    if (!Object.prototype.hasOwnProperty.call(productData, "sku")) {
      delete body.sku;
    }
  }
  return body;
};

export const updateAuthorizeProduct = createAsyncThunk(
  "authorize-products/updateProduct",
  async ({ productId, productData }, { rejectWithValue }) => {
    try {
      const packageUpdate = isAuthorizePackageType(productData?.type);
      const body = packageUpdate
        ? packagingWriteBody(productData, { create: false })
        : authorizeProductUpdateRequestBody(productData);
      const response = await callApi(
        packageUpdate
          ? `/epr/packages/${encodeURIComponent(productId)}`
          : `/products/${productId}`,
        "PUT",
        body
      );

      if (!response.ok) {
        if (packageUpdate && response.status === 403) {
          return rejectWithValue(PACKAGING_WRITE_FORBIDDEN_MESSAGE);
        }
        return rejectWithValue(response.errorMessage || "Error");
      }

      const data = await response.json().catch(() => ({}));
      return data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Error"));
    }
  }
);

export const addAuthorizeProduct = createAsyncThunk(
  "authorize-products/addProduct",
  async (productData, { rejectWithValue }) => {
    try {
      const packageCreate = isAuthorizePackageType(productData?.type);
      const endpoint = packageCreate ? "/epr/packages" : "/products/";
      const response = await callApi(
        endpoint,
        "POST",
        packageCreate
          ? packagingWriteBody(productData, { create: true })
          : authorizeProductRequestBody(productData)
      );

      const responseData = await response.json().catch(() => ({}));

      // Preserve subscription cart body on any HTTP status (same as sendingEinvoices)
      if (responseData?.to_buy) {
        return {
          ...responseData,
          __subscriptionRequired: true,
          __httpStatus: response.status,
        };
      }

      if (!response.ok) {
        if (packageCreate && response.status === 403) {
          return rejectWithValue(PACKAGING_WRITE_FORBIDDEN_MESSAGE);
        }
        return rejectWithValue(response.errorMessage || "Error");
      }

      return responseData;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Error"));
    }
  }
);

const authorizeProductsSlice = createSlice({
  name: "authorizeProducts",
  initialState,
  reducers: {
    clearAddingError: (state) => {
      state.addingProductError = null;
    },
    clearFetchingError: (state) => {
      state.fetchingProductsError = null;
    },
    clearUpdatingStatusError: (state) => {
      state.updatingStatusError = null;
    },
    clearDeletingError: (state) => {
      state.deletingProductError = null;
    },
    clearUpdatingProductError: (state) => {
      state.updatingProductError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAuthorizeProducts.pending, (state) => {
        state.fetchingProducts = true;
        state.fetchingProductsError = null;
      })
      .addCase(fetchAuthorizeProducts.fulfilled, (state, action) => {
        state.fetchingProducts = false;
        state.fetchingProductsError = null;
        state.products = action.payload || [];
      })
      .addCase(fetchAuthorizeProducts.rejected, (state, action) => {
        state.fetchingProducts = false;
        state.fetchingProductsError = action.payload || "Failed to fetch products";
      })
      .addCase(addAuthorizeProduct.pending, (state) => {
        state.addingProduct = true;
        state.addingProductError = null;
      })
      .addCase(addAuthorizeProduct.fulfilled, (state) => {
        state.addingProduct = false;
        state.addingProductError = null;
      })
      .addCase(addAuthorizeProduct.rejected, (state, action) => {
        state.addingProduct = false;
        state.addingProductError = action.payload || "Failed to add product";
      })
      .addCase(updateAuthorizeProduct.pending, (state) => {
        state.updatingProduct = true;
        state.updatingProductError = null;
      })
      .addCase(updateAuthorizeProduct.fulfilled, (state) => {
        state.updatingProduct = false;
        state.updatingProductError = null;
      })
      .addCase(updateAuthorizeProduct.rejected, (state, action) => {
        state.updatingProduct = false;
        state.updatingProductError = action.payload || "Failed to update product";
      })
      .addCase(updateProductStatus.pending, (state) => {
        state.updatingStatus = true;
        state.updatingStatusError = null;
      })
      .addCase(updateProductStatus.fulfilled, (state) => {
        state.updatingStatus = false;
        state.updatingStatusError = null;
      })
      .addCase(updateProductStatus.rejected, (state, action) => {
        state.updatingStatus = false;
        state.updatingStatusError = action.payload || "Failed to update product status";
      })
      .addCase(deleteAuthorizeProduct.pending, (state) => {
        state.deletingProduct = true;
        state.deletingProductError = null;
      })
      .addCase(deleteAuthorizeProduct.fulfilled, (state) => {
        state.deletingProduct = false;
        state.deletingProductError = null;
      })
      .addCase(deleteAuthorizeProduct.rejected, (state, action) => {
        state.deletingProduct = false;
        state.deletingProductError = action.payload || "Failed to delete product";
      })
      .addCase(deleteAuthorizePackage.pending, (state) => {
        state.deletingProduct = true;
        state.deletingProductError = null;
      })
      .addCase(deleteAuthorizePackage.fulfilled, (state) => {
        state.deletingProduct = false;
        state.deletingProductError = null;
      })
      .addCase(deleteAuthorizePackage.rejected, (state, action) => {
        state.deletingProduct = false;
        state.deletingProductError = action.payload || "Failed to delete packaging";
      });
  },
});

export const { 
  clearAddingError, 
  clearFetchingError, 
  clearUpdatingStatusError, 
  clearUpdatingProductError, 
  clearDeletingError 
} = authorizeProductsSlice.actions;
export default authorizeProductsSlice.reducer;
